import {
  SHIPMENT_DISPLAY_STATUS,
  type ShipmentDisplayStatus,
} from '@/utils/shipment-status-ui';
import type { RoutingStrategy } from '@/components/shipment/form/utils/shipment-form-routing';

export type { RoutingStrategy } from '@/components/shipment/form/utils/shipment-form-routing';

export type StopFieldKey =
  | 'location'
  | 'radius'
  | 'plannedDeparture'
  | 'plannedArrival';

export type FieldEditState = {
  editable: boolean;
  lockReason?: string;
  lockBadgeLabel?: string;
};

export type ShipmentEditCapabilities = {
  canAccessPage: boolean;
  canEditMetadata: boolean;
  canEditOrigin: boolean;
  canEditDestination: boolean;
  canEditRoute: boolean;
  canAssignTracker: boolean;
  canPublish: boolean;
  routingStrategy: RoutingStrategy;
  origin: FieldEditState;
  destination: FieldEditState;
  route: FieldEditState;
  tracker: FieldEditState;
};

const ORIGIN_DEPARTED_REASON =
  "Origin and planned departure can't be changed once the shipment has departed. To start from a different origin, create a new shipment.";

const ORIGIN_DEPARTED_LOCK_BADGE = 'Locked - In transit';

const DELIVERED_LOCKED_REASON =
  'This shipment has been delivered. Location and route cannot be changed.';

function allEditable(): FieldEditState {
  return { editable: true };
}

function locked(reason: string, badgeLabel?: string): FieldEditState {
  return {
    editable: false,
    lockReason: reason,
    ...(badgeLabel ? { lockBadgeLabel: badgeLabel } : {}),
  };
}

export function getShipmentEditCapabilities(
  displayStatus: ShipmentDisplayStatus,
): ShipmentEditCapabilities {
  switch (displayStatus) {
    case SHIPMENT_DISPLAY_STATUS.Draft:
      return {
        canAccessPage: true,
        canEditMetadata: true,
        canEditOrigin: true,
        canEditDestination: true,
        canEditRoute: true,
        canAssignTracker: true,
        canPublish: true,
        routingStrategy: 'originToDestination',
        origin: allEditable(),
        destination: allEditable(),
        route: allEditable(),
        tracker: allEditable(),
      };
    case SHIPMENT_DISPLAY_STATUS.Booked:
      return {
        canAccessPage: true,
        canEditMetadata: true,
        canEditOrigin: true,
        canEditDestination: true,
        canEditRoute: true,
        canAssignTracker: false,
        canPublish: false,
        routingStrategy: 'originToDestination',
        origin: allEditable(),
        destination: allEditable(),
        route: allEditable(),
        tracker: locked('The assigned tracker cannot be changed.'),
      };
    case SHIPMENT_DISPLAY_STATUS.Active:
    case SHIPMENT_DISPLAY_STATUS.Late:
      return {
        canAccessPage: true,
        canEditMetadata: true,
        canEditOrigin: false,
        canEditDestination: true,
        canEditRoute: true,
        canAssignTracker: false,
        canPublish: false,
        routingStrategy: 'trackerToDestination',
        origin: locked(ORIGIN_DEPARTED_REASON, ORIGIN_DEPARTED_LOCK_BADGE),
        destination: allEditable(),
        route: allEditable(),
        tracker: locked('The assigned tracker cannot be changed.'),
      };
    case SHIPMENT_DISPLAY_STATUS.Delivered:
      return {
        canAccessPage: true,
        canEditMetadata: true,
        canEditOrigin: false,
        canEditDestination: false,
        canEditRoute: false,
        canAssignTracker: false,
        canPublish: false,
        routingStrategy: 'none',
        origin: locked(DELIVERED_LOCKED_REASON),
        destination: locked(DELIVERED_LOCKED_REASON),
        route: locked(DELIVERED_LOCKED_REASON),
        tracker: locked('The assigned tracker cannot be changed.'),
      };
    case SHIPMENT_DISPLAY_STATUS.Closed:
    default:
      return {
        canAccessPage: false,
        canEditMetadata: false,
        canEditOrigin: false,
        canEditDestination: false,
        canEditRoute: false,
        canAssignTracker: false,
        canPublish: false,
        routingStrategy: 'none',
        origin: locked('This shipment is closed.'),
        destination: locked('This shipment is closed.'),
        route: locked('This shipment is closed.'),
        tracker: locked('This shipment is closed.'),
      };
  }
}

export function isStopFieldEditable(
  capabilities: ShipmentEditCapabilities,
  stopRole: 'origin' | 'destination',
  field: StopFieldKey,
): boolean {
  const group = stopRole === 'origin' ? capabilities.origin : capabilities.destination;
  if (!group.editable) return false;
  if (stopRole === 'origin' && field === 'plannedArrival') return false;
  if (stopRole === 'destination' && field === 'plannedDeparture') return false;
  return true;
}
